import React, {useCallback, useEffect, useMemo, useState} from 'react'
import './ModalSessions.css'
import {format} from 'date-fns'

const ModalSessions = ({closeModal, clientIdSessions, clientInfo}) => {
    const clientId = clientIdSessions;
    const [clientSessions, setClientSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState('');

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setErrors('');

        fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_logs/client/' + clientId + '/list',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Unable to load session history.');
                }

                return res.json();
            })
            .then(data => {
                setClientSessions(Array.isArray(data) ? data : []);
            })
            .catch(error => {
                console.error(error);
                setErrors('Unable to load session history right now.');
            })
            .finally(() => {
                setIsLoading(false);
            });
        }, [clientId]);


    const handleSubmit = (e) => {
        e.preventDefault();
        closeModal();
    }

    const buildUsageSummary = useCallback((items, keyName) => {
        const usageMap = items.reduce((accumulator, item) => {
            const rawValue = item?.[keyName];
            const normalizedValue = String(rawValue ?? '').trim() || 'Unknown';
            accumulator[normalizedValue] = (accumulator[normalizedValue] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(usageMap)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }
                return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
            });
    }, []);

    const protocolUsage = useMemo(
        () => buildUsageSummary(clientSessions, 'name'),
        [buildUsageSummary, clientSessions]
    );

    const facilityUsage = useMemo(
        () => buildUsageSummary(clientSessions, 'facility_name'),
        [buildUsageSummary, clientSessions]
    );

    const getUsageSizing = useCallback((usageItems, labelHeader) => {
        const maxLabelChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.label ?? '').length),
            labelHeader.length
        );
        const maxCountChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.count ?? '').length),
            'Count'.length
        );

        const labelWidthCh = Math.max(8, maxLabelChars + 1);
        const countWidthCh = Math.max(5, maxCountChars + 1);
        const tableWidthCh = labelWidthCh + countWidthCh + 4;

        return {
            labelWidthCh,
            countWidthCh,
            tableWidthCh,
        };
    }, []);

    const protocolUsageSizing = useMemo(
        () => getUsageSizing(protocolUsage, 'Protocol'),
        [getUsageSizing, protocolUsage]
    );

    const facilityUsageSizing = useMemo(
        () => getUsageSizing(facilityUsage, 'Facility'),
        [facilityUsage, getUsageSizing]
    );

    const summaryWidthCh = useMemo(() => {
        const protocolCardWidthCh = protocolUsageSizing.labelWidthCh + protocolUsageSizing.countWidthCh + 9;
        const facilityCardWidthCh = facilityUsageSizing.labelWidthCh + facilityUsageSizing.countWidthCh + 9;
        const cardsGapCh = 4;
        return protocolCardWidthCh + facilityCardWidthCh + cardsGapCh;
    }, [facilityUsageSizing, protocolUsageSizing]);

    const historyWidthCh = useMemo(() => {
        const measure = (value) => String(value ?? '').length;
        const rows = Array.isArray(clientSessions) ? clientSessions : [];

        const columns = [
            {
                header: 'Date',
                value: (row) => format(new Date(row.started), 'MMM d, yyyy'),
                minCh: 12,
                maxCh: 14,
            },
            {
                header: 'Mins',
                value: (row) => row.total_mins,
                minCh: 6,
                maxCh: 8,
            },
            {
                header: 'Protocol',
                value: (row) => row.name,
                minCh: 12,
                maxCh: 32,
            },
            {
                header: 'Facility',
                value: (row) => row.facility_name,
                minCh: 12,
                maxCh: 26,
            },
        ];

        return columns.reduce((total, column) => {
            const longestContent = rows.reduce((longest, row) => {
                return Math.max(longest, measure(column.value(row)));
            }, measure(column.header));

            const paddedCh = longestContent + 1;
            const widthCh = Math.max(column.minCh, Math.min(column.maxCh, paddedCh));

            return total + widthCh;
        }, 0);
    }, [clientSessions]);

    const modalContentWidthCh = Math.max(summaryWidthCh, historyWidthCh);

    const modalWidthStyle = {
        width: `min(calc(${modalContentWidthCh}ch + 5.4rem), 96vw)`,
    };

    const lastSessionDate = useMemo(() => {
        if (!Array.isArray(clientSessions) || clientSessions.length === 0) {
            return null;
        }

        const latestTimestamp = clientSessions.reduce((latest, session) => {
            const timestamp = Date.parse(session?.started);
            if (Number.isNaN(timestamp)) {
                return latest;
            }
            return Math.max(latest, timestamp);
        }, Number.NEGATIVE_INFINITY);

        if (!Number.isFinite(latestTimestamp)) {
            return null;
        }

        return format(new Date(latestTimestamp), 'MMM d, yyyy');
    }, [clientSessions]);

    const showLoadedContent = !isLoading;

    return (
    <div className='modal-table'>
            <div className='modal-sessions modal-client-history-sessions' style={modalWidthStyle}>
                <div className='modal-sessions-header'>
                    {showLoadedContent ? (
                        <>
                            <h3>Session History</h3>
                            <p>
                                {clientInfo?.first_name} {clientInfo?.last_name} | Client ID: {clientInfo?.id}
                            </p>
                            <div className='modal-sessions-chip-row'>
                                <div className='modal-sessions-count'>
                                    {clientSessions.length} {clientSessions.length === 1 ? 'session' : 'sessions'}
                                </div>
                                <div className='modal-sessions-count'>Last Session: {lastSessionDate ?? 'N/A'}</div>
                            </div>
                        </>
                    ) : (
                        <div className='modal-sessions-header-skeleton'>
                            <span className='table-skeleton-line modal-header-skeleton-title' />
                            <span className='table-skeleton-line modal-header-skeleton-subtitle' />
                            <span className='table-skeleton-line modal-header-skeleton-pill' />
                        </div>
                    )}
                </div>

                {errors && <div className='error'>{errors}</div>}

                {showLoadedContent && !errors && (
                    <div className='modal-sessions-usage-summary' style={{ flexWrap: 'nowrap', overflow: 'visible' }}>
                        <div className='usage-card'>
                            <div className='usage-card-title'>Protocols ({protocolUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {protocolUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${protocolUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${protocolUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Protocol</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {protocolUsage.map((item) => (
                                                <tr key={`protocol-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No protocol usage found.</span>
                                )}
                            </div>
                        </div>

                        <div className='usage-card'>
                            <div className='usage-card-title'>Facilities ({facilityUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {facilityUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${facilityUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${facilityUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Facility</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {facilityUsage.map((item) => (
                                                <tr key={`facility-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No facility usage found.</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className='table-wrapper modal-sessions-table-scroll'>
                    <table className='table-sessions'>
                        <thead>
                            <tr>
                                <th className='center'>Date</th>
                                <th className='center'>Mins</th>
                                <th className='center'>Protocol</th>
                                <th className='center'>Facility</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 7 }).map((_, rowIndex) => (
                                    <tr key={`client-sessions-skeleton-${rowIndex}`} className='table-skeleton-row'>
                                        <td><span className='table-skeleton-line short' /></td>
                                        <td><span className='table-skeleton-line short' /></td>
                                        <td><span className='table-skeleton-line' /></td>
                                        <td><span className='table-skeleton-line' /></td>
                                    </tr>
                                ))
                            ) : clientSessions.length > 0 ? (
                                clientSessions.map((clientSession, i) => {
                                    return <tr key={i}>
                                        <td className='center'>{format(new Date(clientSession.started), 'MMM d, yyyy')}</td>
                                        <td className='center'>{clientSession.total_mins}</td>
                                        <td className='left'>{clientSession.name}</td>
                                        <td className='left'>{clientSession.facility_name}</td>
                                    </tr>
                                })
                            ) : (
                                <tr>
                                    <td className='center modal-sessions-empty' colSpan={4}>No session history found for this client.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <button type='submit' className='btn' onClick={handleSubmit}>Close</button>
            </div>
        </div>
  )
}

export default ModalSessions